// Cron service for managing scheduled security scans
import authService from './authService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://138.199.234.61:8080';
const PENTEST_API_PREFIX = process.env.REACT_APP_PENTEST_GATEWAY_PREFIX || '/api/pentest';
// Safe join to avoid double "/api" when base ends with "/api" and prefix starts with "/api/"
function joinBaseAndPrefix(base, prefix) {
  const b = (base || '').replace(/\/+$/, '');
  let p = (prefix || '').replace(/^\/+/, '/');
  if (!p.startsWith('/')) p = `/${p}`;
  if (b.endsWith('/api') && p.startsWith('/api/')) {
    return `${b.slice(0, -4)}${p}`;
  }
  return `${b}${p}`;
}

const PENTEST_API_BASE_URL = joinBaseAndPrefix(API_BASE, PENTEST_API_PREFIX);

class CronService {
    constructor() {
        this.baseURL = '/api/cron';
    }

    // Make authenticated call to pentest-api server
    async pentestAuthenticatedCall(endpoint, options = {}) {
        const token = authService.getToken();
        const userInfo = authService.getCurrentUser();
        
        if (!token) {
            throw new Error('No authentication token available');
        }

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers
        };

        // Add user info headers for pentest-api server
        if (userInfo) {
            headers['x-user-name'] = userInfo.username;
            headers['x-user-id'] = userInfo.id;
            headers['x-user-role'] = userInfo.role;
        }

        const response = await fetch(`${PENTEST_API_BASE_URL}${endpoint}`, {
            ...options,
            headers
        });

        if (response.status === 401) {
            authService.logout();
            window.location.href = '/pages/login';
            throw new Error('Authentication failed');
        }

        return response;
    }

    // Transform API response from snake_case to camelCase
    transformTaskFromAPI(task) {
        return {
            id: task.id,
            name: task.name,
            description: task.description,
            targetUrl: task.target_url,
            selectedTools: task.selected_tools,
            cronExpression: task.cron_expression,
            isEnabled: Boolean(task.is_enabled),
            createdBy: task.created_by,
            createdAt: task.created_at,
            updatedAt: task.updated_at,
            lastRunAt: task.last_run_at,
            nextRun: task.next_run_at,
            scanOptions: task.scan_options,
            executionCount: task.execution_count || 0,
            successCount: task.success_count || 0,
            failureCount: task.failure_count || 0,
            lastExecution: task.last_execution
        };
    }

    // Task Management
    async getTasks() {
        try {
            const response = await this.pentestAuthenticatedCall('/cron/tasks');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            const tasks = data.data || data;
            // Transform each task from snake_case to camelCase
            return Array.isArray(tasks) ? tasks.map(task => this.transformTaskFromAPI(task)) : [];
        } catch (error) {
            console.error('Error fetching cron tasks:', error);
            throw error;
        }
    }

    async createTask(taskData) {
        try {
            const response = await this.pentestAuthenticatedCall('/cron/tasks', {
                method: 'POST',
                body: JSON.stringify(taskData)
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to create task: ${errorData}`);
            }
            
            const data = await response.json();
            const task = data.data || data;
            return task ? this.transformTaskFromAPI(task) : null;
        } catch (error) {
            console.error('Error creating cron task:', error);
            throw error;
        }
    }

    async updateTask(taskId, taskData) {
        try {
            const response = await this.pentestAuthenticatedCall(`/cron/tasks/${taskId}`, {
                method: 'PUT',
                body: JSON.stringify(taskData)
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to update task: ${errorData}`);
            }
            
            const data = await response.json();
            const task = data.data || data;
            return task ? this.transformTaskFromAPI(task) : null;
        } catch (error) {
            console.error('Error updating cron task:', error);
            throw error;
        }
    }

    async deleteTask(taskId) {
        try {
            const response = await this.pentestAuthenticatedCall(`/cron/tasks/${taskId}`, {
                method: 'DELETE'
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to delete task: ${errorData}`);
            }
            
            return true;
        } catch (error) {
            console.error('Error deleting cron task:', error);
            throw error;
        }
    }

    async toggleTask(taskId, enabled) {
        try {
            const response = await this.pentestAuthenticatedCall(`/cron/tasks/${taskId}`, {
                method: 'PUT',
                body: JSON.stringify({ isEnabled: enabled })
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to toggle task: ${errorData}`);
            }
            
            const data = await response.json();
            const task = data.data || data;
            return task ? this.transformTaskFromAPI(task) : null;
        } catch (error) {
            console.error('Error toggling cron task:', error);
            throw error;
        }
    }

    async triggerTask(taskId) {
        try {
            const response = await this.pentestAuthenticatedCall(`/cron/tasks/${taskId}/trigger`, {
                method: 'POST'
            });
            
            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to trigger task: ${errorData}`);
            }
            
            const data = await response.json();
            return data.data || data;
        } catch (error) {
            console.error('Error triggering cron task:', error);
            throw error;
        }
    }

    // Execution Management
    async getExecutions(taskId, limit = 50) {
        try {
            const response = await this.pentestAuthenticatedCall(`/cron/tasks/${taskId}/executions?limit=${limit}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data.data || data;
        } catch (error) {
            console.error('Error fetching executions:', error);
            throw error;
        }
    }

    async getExecutionResults(executionId) {
        try {
            const response = await this.pentestAuthenticatedCall(`/cron/executions/${executionId}/results`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data.data || data;
        } catch (error) {
            console.error('Error fetching execution results:', error);
            throw error;
        }
    }

    // Validation
    validateCronExpression(expression) {
        // Basic cron validation - 5 fields separated by spaces
        if (!expression || typeof expression !== 'string') {
            return false;
        }
        
        const parts = expression.trim().split(/\s+/);
        if (parts.length !== 5) {
            return false;
        }
        
        // Basic regex for each field
        const patterns = [
            /^(\*|([0-9]|[1-5][0-9])|\*\/([0-9]|[1-5][0-9]))$/, // minute (0-59)
            /^(\*|([0-9]|1[0-9]|2[0-3])|\*\/([0-9]|1[0-9]|2[0-3]))$/, // hour (0-23)
            /^(\*|([1-9]|[12][0-9]|3[01])|\*\/([1-9]|[12][0-9]|3[01]))$/, // day (1-31)
            /^(\*|([1-9]|1[0-2])|\*\/([1-9]|1[0-2]))$/, // month (1-12)
            /^(\*|[0-6]|\*\/[0-6])$/ // day of week (0-6)
        ];
        
        return parts.every((part, index) => patterns[index].test(part));
    }

    // Helper to get human-readable cron description
    describeCronExpression(expression) {
        if (!this.validateCronExpression(expression)) {
            return 'Invalid cron expression';
        }
        
        const [minute, hour, day, month, dayOfWeek] = expression.split(' ');
        
        // Handle interval patterns first
        if (minute.startsWith('*/')) {
            const interval = minute.substring(2);
            return `Every ${interval} minutes`;
        }

        // Specific times and patterns
        if (minute !== '*' && hour !== '*' && day === '*' && month === '*' && dayOfWeek === '*') {
            return `Every day at ${this.formatTime(hour, minute)}`;
        }
        
        if (minute !== '*' && hour !== '*' && day !== '*' && month === '*' && dayOfWeek === '*') {
            return `Every month on day ${day} at ${this.formatTime(hour, minute)}`;
        }
        
        if (minute !== '*' && hour !== '*' && day === '*' && month === '*' && dayOfWeek !== '*') {
            return `Every ${this.getDayName(dayOfWeek)} at ${this.formatTime(hour, minute)}`;
        }

        if (minute === '0' && hour === '0' && day === '*' && month === '*' && dayOfWeek === '*') {
            return 'Every day at midnight';
        }

        if (minute === '0' && hour === '12' && day === '*' && month === '*' && dayOfWeek === '*') {
            return 'Every day at noon';
        }

        if (minute === '0' && hour === '*' && day === '*' && month === '*' && dayOfWeek === '*') {
            return 'Every hour on the hour';
        }

        return 'Custom schedule';
    }

    formatTime(hour, minute) {
        const h = parseInt(hour, 10);
        const m = parseInt(minute, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 || 12;
        const minuteStr = m.toString().padStart(2, '0');
        return `${hour12}:${minuteStr} ${ampm}`;
    }

    getDayName(dayOfWeek) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const d = parseInt(dayOfWeek, 10);
        return days[d] || 'Unknown day';
    }

    getOrdinalNumber(num) {
        const n = parseInt(num, 10);
        if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
        switch (n % 10) {
            case 1: return `${n}st`;
            case 2: return `${n}nd`;
            case 3: return `${n}rd`;
            default: return `${n}th`;
        }
    }

    getPresetExpressions() {
        return [
            { label: 'Every 5 minutes', value: '*/5 * * * *' },
            { label: 'Every 15 minutes', value: '*/15 * * * *' },
            { label: 'Hourly', value: '0 * * * *' },
            { label: 'Daily at midnight', value: '0 0 * * *' },
            { label: 'Daily at 6 AM', value: '0 6 * * *' },
            { label: 'Weekly (Sunday at midnight)', value: '0 0 * * 0' },
            { label: 'Monthly (1st at midnight)', value: '0 0 1 * *' },
        ];
    }

    // Helper to format date for UI display
    formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleString();
    }

    // Helper to color-code status strings
    getStatusColor(status) {
        const colors = {
            'queued': 'secondary',
            'running': 'warning',
            'completed': 'success',
            'failed': 'danger',
            'cancelled': 'gray-500'
        };
        return colors[status] || 'gray-500';
    }
}

export default new CronService();